import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CaretRightIcon } from '@phosphor-icons/react';

import { cn } from '@/lib/utils';

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Trigger
        ref={ref}
        className={cn('outline-none', className)}
        {...props}
    />
));
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

const DropdownMenuGroup = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.Group>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Group>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Group
        ref={ref}
        className={cn('flex flex-col gap-1', className)}
        {...props}
    />
));
DropdownMenuGroup.displayName = DropdownMenuPrimitive.Group.displayName;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuSubTrigger = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
        variant?: 'default' | 'danger';
    }
>(({ className, variant = 'default', children, ...props }, ref) => (
    <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        className={cn(
            'flex items-center gap-1.5 py-1.5 pl-2 pr-1.5 min-h-8 rounded-[5px] outline-none cursor-pointer transition-colors',
            'text-[14px] leading-5 font-normal data-[disabled]:pointer-events-none',
            variant === 'default' &&
                'text-white [&_svg]:text-grey-200 hover:bg-surface-hover-strong focus:bg-surface-hover-strong data-[state=open]:bg-surface-hover-strong data-[disabled]:text-grey-300 data-[disabled]:opacity-100',
            variant === 'danger' &&
                'text-danger-200 hover:bg-danger-200/10 focus:bg-danger-200/10 data-[state=open]:bg-danger-200/10 data-[disabled]:text-grey-500',
            className,
        )}
        {...props}
    >
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
            {children}
        </span>
        <CaretRightIcon size={16} weight="regular" className="shrink-0" />
    </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
    DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
        variant?: 'default' | 'compact';
    }
>(({ className, variant = 'default', sideOffset = 8, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.SubContent
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                'z-50',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
                'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
                'ml-1',
                variant === 'default' &&
                    'box-border bg-grey-600 border border-grey-500 rounded-[10px] min-w-[250px] p-2 gap-2.5 flex flex-col shadow-dropdown-medium',
                variant === 'compact' &&
                    'bg-grey-600 border border-grey-500 rounded-[10px] min-w-[160px] p-2 gap-2.5 flex flex-col shadow-dropdown-medium',
                className,
            )}
            {...props}
        />
    </DropdownMenuPrimitive.Portal>
));
DropdownMenuSubContent.displayName =
    DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
        variant?: 'default' | 'compact';
    }
>(({ className, variant = 'default', sideOffset = 8, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                'z-50',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
                'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
                variant === 'default' &&
                    'box-border bg-grey-600 border border-grey-500 rounded-[10px] min-w-[250px] p-2 gap-1 flex flex-col shadow-dropdown-medium',
                variant === 'compact' &&
                    'bg-grey-600 border border-grey-500 rounded-[10px] min-w-[160px] p-2 gap-1 flex flex-col shadow-dropdown-medium',
                className,
            )}
            {...props}
        />
    </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItemBase = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
        variant?: 'default' | 'danger';
    }
>(({ className, variant = 'default', ...props }, ref) => (
    <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
            'flex items-center gap-1.5 py-1.5 pl-2 pr-1.5 min-h-8 rounded-[5px] outline-none cursor-pointer transition-colors',
            'text-[14px] leading-5 font-normal data-[disabled]:pointer-events-none',
            variant === 'default' &&
                'text-white [&_svg]:text-grey-200 hover:bg-surface-hover-strong focus:bg-surface-hover-strong data-[disabled]:text-grey-300 data-[disabled]:opacity-100',
            variant === 'danger' &&
                'text-danger-200 hover:bg-danger-200/10 focus:bg-danger-200/10 data-[disabled]:text-grey-500',
            className,
        )}
        {...props}
    />
));
DropdownMenuItemBase.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuLabel = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
        ref={ref}
        className={cn(
            'pt-2 px-2 pb-1.5 text-[14px] leading-5 font-medium text-grey-300',
            className,
        )}
        {...props}
    />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
    React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator
        ref={ref}
        className={cn('h-px my-1 bg-grey-500', className)}
        {...props}
    />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

export {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
};
